"use client";

import { Slider } from "@/components/ui/slider";
import { useEffect, useState } from "react";
import { formatPrice } from "@/utils/format-price";

const CheckIcon = () => (
    <svg className="shrink-0" width="32" height="32" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12.5" cy="12.5" r="12.5" fill="#7E869E" fillOpacity="0.25" />
        <path d="M6.8182 11.0795L11.2544 15.793C11.3136 15.8559 11.4137 15.8559 11.4729 15.793L23.4849 3.03027" stroke="#222222" strokeWidth="2" />
        <path d="M23.782 10.2714C24.2912 12.8492 23.9023 15.5237 22.6799 17.8497C21.4576 20.1757 19.4755 22.013 17.0637 23.0557C14.6518 24.0985 11.9555 24.2838 9.42366 23.5809C6.89178 22.878 4.67698 21.3292 3.14784 19.1923C1.61871 17.0554 0.867483 14.4593 1.01918 11.8361C1.17088 9.21283 2.21635 6.72063 3.98161 4.77426C5.74687 2.82789 8.12544 1.54474 10.7215 1.13836C13.3175 0.731984 15.9744 1.22689 18.25 2.54071" stroke="#222222" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export interface FinanziamentoProps {
    prezzoAuto: number;
    articolo: any;
    km: number;
    tassoAgevolato?: boolean;
    onChangeRate?: (rate: number) => void;
}

const getMinDurata = (importo: number): number => {
    if (importo < 7500) return 3; // 6000–7499 € → minimo 36 mesi (3 anni)
    return 2;                      // ≥ 7500 € → minimo 24 mesi (2 anni)
};

export default function Finanziamento({ prezzoAuto, articolo, km, tassoAgevolato = false, onChangeRate }: FinanziamentoProps) {

    const [anticipo, setAnticipo] = useState(0);
    const [durata, setDurata] = useState(8);
    const [rata, setRata] = useState(0);
    
    const maxAnticipo = prezzoAuto - 6000;

    const spesaIstruttoria = 350;
    const impostaBollo = 16;
    const tasso = (km ?? 0) <= 5000
        ? (tassoAgevolato ? 6.95 : 7.95)
        : (tassoAgevolato ? 7.95 : 8.95);

    const minDurata = getMinDurata(prezzoAuto - anticipo);
    const effectiveDurata = Math.max(durata, minDurata);

    useEffect(() => {
        const prezzoFinanziato = (prezzoAuto - anticipo) + spesaIstruttoria + impostaBollo;
        const interessi = tasso / 100 / 12;
        const mensilita = effectiveDurata * 12;
        const costante = Math.pow(1 + interessi, mensilita);
        const rataMensile = (prezzoFinanziato * costante * interessi) / (costante - 1);
        setRata(rataMensile);
        
        if (durata !== effectiveDurata) {
            setDurata(effectiveDurata);
        }
    }, [anticipo, durata, effectiveDurata, prezzoAuto, tasso]);

    const bullets = [
        "Acquisto a distanza",
        "Preventivo senza impegno",
        "Permuta dell'usato",
        "Spedizione in tutta Italia",
    ];

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">

            <div className="mb-6">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Finanziamento</p>
                <h4 className="text-xl font-bold text-gray-900">Calcola la tua rata</h4>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-6">

                {/* Feature bullets */}
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-y-4 gap-x-4 content-start">
                    {bullets.map((label) => (
                        <div key={label} className="flex items-center gap-3">
                            <CheckIcon />
                            <p className="text-sm text-gray-600">{label}</p>
                        </div>
                    ))}
                </div>

                {/* Sliders */}
                <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-700">Anticipo</p>
                            <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                                {formatPrice(anticipo)}
                            </span>
                        </div>
                        <Slider
                            value={[anticipo]}
                            min={0}
                            max={maxAnticipo}
                            step={100}
                            onValueChange={(v) => setAnticipo(v[0])}
                        />
                        <div className="mt-2 flex justify-between text-xs text-gray-400">
                            <span>0 €</span>
                            <span>{formatPrice(maxAnticipo)}</span>
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-700">Durata</p>
                            <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                                {effectiveDurata} {effectiveDurata === 1 ? "anno" : "anni"}
                            </span>
                        </div>
                        <Slider
                            value={[effectiveDurata]}
                            min={minDurata}
                            max={8}
                            step={1}
                            onValueChange={(v) => setDurata(v[0])}
                        />
                        <div className="mt-2 flex justify-between text-xs text-gray-400">
                            <span>{minDurata} {minDurata === 1 ? "anno" : "anni"}</span>
                            <span>8 anni</span>
                        </div>
                    </div>
                </div>

                {/* Rata card */}
                <div className="flex flex-col justify-center rounded-2xl border border-primary/30 bg-primary/5 p-5">
                    <p className="text-xs font-bold text-primary uppercase tracking-widest">Rata mensile stimata</p>
                    <div className="mt-3 flex items-end gap-1.5">
                        <h4 className="text-4xl font-bold text-gray-900 leading-none">{formatPrice(rata)}</h4>
                        <span className="text-sm text-gray-500 mb-0.5">/mese</span>
                    </div>
                    <p className="mt-3 text-xs text-gray-500">
                        TAN {tasso}% — importo indicativo.
                    </p>
                    <div className="mt-3 pt-3 border-t border-primary/15">
                        <p className="text-[11px] leading-relaxed text-gray-400">
                            Salvo approvazione della Finanziaria. Fogli informativi disponibili presso la Concessionaria.
                        </p>
                    </div>
                </div>

            </div>

        </div>
    );
}
